/**
 * Centralized error helpers for athlete app.
 *
 * Why this module exists (Cycle 14 / F.1):
 *   • Until now каждый API caller имел inline `catch (e: any) {
 *     setErr(e?.response?.data?.detail || "error") }` — 30+ duplicates.
 *   • `.catch(() => {})` без feedback тоже массово рассыпан (39 hits).
 *   • Без unified shape errors → drift в `any` typing.
 *
 * Two helpers:
 *
 *   • extractApiError(e: unknown): string
 *       Превращает axios / network / unknown thrown в одну string. Берёт
 *       `response.data.detail` (FastAPI стандарт), fallback `.message`,
 *       fallback "error".
 *
 *   • silentTrack(fn: () => Promise<unknown>, label: string): Promise<void>
 *       Запускает fire-and-forget API call. Если падает — НЕ показывает
 *       error athlete'у (для tracking / non-critical signals), но шлёт
 *       Plausible event 'silent_failure' для observability. Так мы
 *       не теряем сигнал о backend dropoff.
 *
 * Pattern гайды:
 *   • PATCH/POST который athlete видит → try { await ... } catch (e) {
 *       setErr(extractApiError(e)) }
 *   • Background tracking (intro_seen, view_event) → silentTrack(() =>
 *       api.post(...), "intro_seen")
 *   • Video.play / autoplay restrictions → оставить `.catch(() => {})` —
 *       browser-mandated suppression, не silent bug.
 */

interface AxiosLikeError {
  response?: {
    data?: { detail?: string | unknown[] | Record<string, unknown> };
    status?: number;
  };
  message?: string;
}

/** Robustly extract a human-readable error string from an unknown thrown. */
export function extractApiError(e: unknown, fallback = "error"): string {
  if (e == null) return fallback;
  if (typeof e === "string") return e;
  if (e instanceof Error && !("response" in e)) {
    return e.message || fallback;
  }
  const ax = e as AxiosLikeError;
  const detail = ax?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  // FastAPI validation errors: detail is an array of { msg, loc, type }.
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: string };
    if (typeof first?.msg === "string") return first.msg;
  }
  if (typeof ax?.message === "string" && ax.message) return ax.message;
  return fallback;
}

/** Status code of an axios-like error if available, else null. */
export function getStatusCode(e: unknown): number | null {
  const ax = e as AxiosLikeError;
  const status = ax?.response?.status;
  return typeof status === "number" ? status : null;
}

/**
 * Fire-and-forget wrapper. The Promise NEVER rejects.
 *
 * On failure: emits a Plausible 'silent_failure' event with the supplied
 * label so we still see backend dropoff in aggregates. The label SHOULD
 * be a short identifier like 'intro_seen' or 'profile_view'.
 */
export async function silentTrack(
  fn: () => Promise<unknown>,
  label: string,
): Promise<void> {
  try {
    await fn();
  } catch (e) {
    // Emit a minimal observability signal so the failure rate is visible
    // in dashboards even though the user is unaware.
    if (typeof window !== "undefined" && typeof window.plausible === "function") {
      try {
        window.plausible("silent_failure", {
          props: { label, code: getStatusCode(e) ?? 0 },
        });
      } catch {
        // silent: don't escalate plausible's own failure
      }
    }
  }
}
